import React, { Children, cloneElement, isValidElement } from 'react'
import type { ReactElement, PropsWithChildren } from 'react'

export interface ButtonGroupProps {
  color?: ButtonGroupColor
  disabled?: boolean
  size?: ButtonGroupSize
  variant?: ButtonGroupVariant
}

export type ButtonGroupColor = 'default' | 'inverse' | 'primary' | 'secondary'

export type ButtonGroupSize = 'large' | 'medium' | 'small'

export type ButtonGroupVariant = 'contained' | 'outlined'

function ButtonGroup({
  children,
  color: groupColor = 'default',
  disabled: groupDisabled = false,
  size: groupSize = 'medium',
  variant: groupVariant = 'contained',
}: PropsWithChildren<ButtonGroupProps>): JSX.Element {
  const elements = Children.toArray(children)
  const number = Children.count(elements)

  return (
    <div className="flex" role="group">
      {elements.map((child: ReactElement, index: number) => {
        if (child) {
          const color: ButtonGroupColor = child.props.color || groupColor
          const disabled = child.props.disabled || groupDisabled
          const size = child.props.size || groupSize
          const variant: ButtonGroupVariant =
            child.props.variant || groupVariant
          const position =
            index === 0 ? 'left' : index === number - 1 ? 'right' : 'middle'

          if (isValidElement(child)) {
            return cloneElement(child as ReactElement, {
              color: color,
              disabled: disabled,
              position: position,
              size: size,
              variant: variant,
            })
          }
        }
      })}
    </div>
  )
}

export default ButtonGroup
